import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import {
  Card,
  Table,
  Stack,
  Button,
  TableBody,
  Container,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Collapse,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Tab,
  Tabs,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Download as DownloadIcon,
  Preview as PreviewIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

// routes
import { PATH_DASHBOARD } from "../../../routes/paths";
// @types
import { ReportPreview, DownloadReport } from "../../../@types/reporting";
import { Brands } from "../../../@types/brand.type";
// components
import CustomBreadcrumbs from "../../../components/custom-breadcrumbs";
import { useSettingsContext } from "../../../components/settings";
// sections
import {
  QueryBuilder,
  DashboardWidgets,
} from "../../../sections/@dashboard/reporting";
import { reportingService } from "../../../api/reporting.service";
import { trackReportTask } from "../../../utils/reportTaskTracker";
import { useAppContext } from "../../../providers/AppContext";
import { BrandService } from "../../../api/brand.service";

// Report categories data
const REPORT_CATEGORIES = [
  {
    title: "Financial Reports",
    icon: "💰",
    sections: [
      {
        title: "Revenue Reports",
        items: [
          "Total revenue (monthly, quarterly, annually, daily, weekly)",
          "Revenue by service type (eye tests, glasses, contact lenses, repairs, etc)",
          "Revenue by practitioner",
        ],
      },
      {
        title: "Payment Reports",
        items: ["Payment methods breakdown"],
      },
      {
        title: "Accounts Receivable/Payable",
        items: ["Voucher liabilities"],
      },
      {
        title: "Operational Finance",
        items: ["Till reconciliation"],
      },
    ],
  },
  {
    title: "Patient Reports",
    icon: "👥",
    sections: [
      {
        title: "Appointment Trends",
        items: [
          "Number of appointments booked, attended, missed, and cancelled",
          "Peak appointment times/days",
        ],
      },
      {
        title: "Patient Demographics",
        items: [
          "Breakdown by age",
          "New vs returning patients",
          "Postcode breakdown",
        ],
      },
      {
        title: "Patient Analytics",
        items: ["Top spenders"],
      },
    ],
  },
  {
    title: "Clinical Reports",
    icon: "🩺",
    sections: [
      {
        title: "Treatment Insights",
        items: ["Clinical activity and outcomes"],
      },
      {
        title: "Clinical Activity",
        items: ["VA input/output", "Prescription reports", "Clinical billing"],
      },
      {
        title: "Practitioner Performance",
        items: [
          "Appointments handled per practitioner",
          "Practitioner clinical efficiency",
        ],
      },
    ],
  },
  {
    title: "Optical Sales Reports",
    icon: "👓",
    sections: [
      {
        title: "Product Sales",
        items: ["General product sales"],
      },
      {
        title: "Inventory Reports",
        items: ["Stock status and levels"],
      },
      {
        title: "Lab & Dispensing",
        items: ["Sales report", "Customer due report", "Pending orders"],
      },
      {
        title: "Purchasing",
        items: ["Purchase reports"],
      },
    ],
  },
  {
    title: "Operational Reports",
    icon: "⚙️",
    sections: [
      {
        title: "Efficiency Metrics",
        items: ["Practitioner utilization rates"],
      },
      {
        title: "Appointment Analysis",
        items: ["Failure rate", "Reason analysis", "DNA Report rate"],
      },
    ],
  },
  {
    title: "Marketing Reports",
    icon: "📊",
    sections: [
      {
        title: "Campaign Insights",
        items: ["Lead source analysis"],
      },
    ],
  },
];
export default function ReportingPage() {
  const { themeStretch } = useSettingsContext();
  const { clinics, appointmentTypes, practitioners } = useAppContext();

  // State management
  const [currentTab, setCurrentTab] = useState("dashboard");
  const [expandedAccordions, setExpandedAccordions] = useState<{
    [key: string]: boolean;
  }>({});
  const [brands, setBrands] = useState<Brands[]>([]);

  const [downloadingReport, setDownloadingReport] = useState<string | null>(
    null,
  );
  const [previewingReport, setPreviewingReport] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<ReportPreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Filter states
  const [reportFilters, setReportFilters] = useState({
    dateFrom: "",
    dateTo: "",
    practitionerId: 0,
    locationId: 0,
    serviceType: "",
    patientId: 0,
    gender: "",
    brand: "",
    reportFormat: "pdf" as "pdf" | "excel" | "csv",
  });

  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    new BrandService()
      .getAllBrands()
      .then((res) => setBrands(res.data ?? []))
      .catch(() => setBrands([]));
  }, []);

  // Utility functions
  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning" = "success",
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const hideSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const validateFilters = () => {
    const errors: string[] = [];

    if (reportFilters.dateFrom && reportFilters.dateTo) {
      const fromDate = new Date(reportFilters.dateFrom);
      const toDate = new Date(reportFilters.dateTo);
      const today = new Date();

      if (fromDate > toDate) {
        errors.push("From date must be before to date");
      }

      if (toDate > today) {
        errors.push("To date cannot be in the future");
      }
    }

    return errors;
  };

  const clearReportFilters = () => {
    setReportFilters({
      dateFrom: "",
      dateTo: "",
      practitionerId: 0,
      locationId: 0,
      serviceType: "",
      patientId: 0,
      gender: "",
      brand: "",
      reportFormat: "pdf",
    });
  };

  // Report operations
  const handleStandardReportDownload = async (
    sectionTitle: string,
    categoryTitle: string,
    reportItem: string,
    format: "pdf" | "excel" | "csv" = "pdf",
  ) => {
    const downloadKey = `${categoryTitle}-${sectionTitle}-${reportItem}`;
    setDownloadingReport(downloadKey);

    try {
      // Validate filters
      const validationErrors = validateFilters();
      if (validationErrors.length > 0) {
        showSnackbar(validationErrors.join(", "), "error");
        setDownloadingReport(null);
        return;
      }

      const downloadData: DownloadReport = {
        reportCategory: categoryTitle,
        reportSection: sectionTitle,
        reportName: reportItem,
        format: format,
        filters: {
          ...reportFilters,
          dateFrom: reportFilters.dateFrom || undefined,
          dateTo: reportFilters.dateTo || undefined,
          practitionerId: reportFilters.practitionerId || undefined,
          patientId: reportFilters.patientId || undefined,
          locationId: reportFilters.locationId || undefined,
        },
      };

      await reportingService.downloadReport(downloadData);
      showSnackbar(
        `${reportItem} downloaded successfully as ${format.toUpperCase()}`,
        "success",
      );
    } catch (error: any) {
      console.error("Error downloading report:", error);

      // Fallback to async download if it takes too long or fails
      if (error.response?.status === 504 || error.code === "ECONNABORTED") {
        showSnackbar(
          "Report is taking longer than expected. Retrying in background...",
          "info",
        );
        handleAsyncReportDownload(
          sectionTitle,
          categoryTitle,
          reportItem,
          format,
        );
      } else {
        const errorMessage =
          error.response?.data?.message || error.message || "Unknown error";
        showSnackbar(`Failed to download report: ${errorMessage}`, "error");
      }
    } finally {
      setDownloadingReport(null);
    }
  };

  const handleAsyncReportDownload = async (
    sectionTitle: string,
    categoryTitle: string,
    reportItem: string,
    format: "pdf" | "excel" | "csv" = "pdf",
  ) => {
    try {
      const downloadData: DownloadReport = {
        reportCategory: categoryTitle,
        reportSection: sectionTitle,
        reportName: reportItem,
        format: format,
        filters: reportFilters,
      };

      const task = await reportingService.asyncDownloadReport(downloadData);
      const taskId = task?.id ?? task?.data?.id;
      if (!taskId) {
        throw new Error("Failed to start background report task");
      }
      trackReportTask(taskId, reportItem, format);
      showSnackbar(
        "Report generation started in background. You will be notified when it is ready.",
        "info",
      );
    } catch (error: any) {
      showSnackbar("Failed to start background report generation", "error");
    }
  };

  const handlePreviewReport = async (
    sectionTitle: string,
    categoryTitle: string,
    reportItem: string,
  ) => {
    const previewKey = `${categoryTitle}-${sectionTitle}-${reportItem}`;
    setPreviewingReport(previewKey);
    try {
      // Validate filters
      const validationErrors = validateFilters();
      if (validationErrors.length > 0) {
        showSnackbar(validationErrors.join(", "), "error");
        return;
      }

      const previewData = await reportingService.previewReport({
        reportCategory: categoryTitle,
        reportSection: sectionTitle,
        reportName: reportItem,
        filters: {
          ...reportFilters,
          dateFrom: reportFilters.dateFrom || undefined,
          dateTo: reportFilters.dateTo || undefined,
          practitionerId: reportFilters.practitionerId
            ? Number(reportFilters.practitionerId)
            : undefined,
          patientId: reportFilters.patientId
            ? Number(reportFilters.patientId)
            : undefined,
          locationId: reportFilters.locationId
            ? Number(reportFilters.locationId)
            : undefined,
        },
      });

      setPreviewData(previewData);
      setPreviewOpen(true);
    } catch (error: any) {
      console.error("Error previewing report:", error);
      showSnackbar(
        `Failed to preview report: ${error.message || "Unknown error"}`,
        "error",
      );
    } finally {
      setPreviewingReport(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Reporting | OptoPro</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <CustomBreadcrumbs
          heading="Reporting"
          links={[
            {
              name: "Dashboard",
              href: PATH_DASHBOARD.root,
            },
            {
              name: "Reporting",
            },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              <Tabs
                value={currentTab}
                onChange={(e, v) => setCurrentTab(v)}
                sx={{
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}>
                <Tab label="Dashboard" value="dashboard" />
                <Tab label="Standard Reports" value="standard" />
                <Tab label="Custom Query" value="custom" />
              </Tabs>
            </Stack>
          }
        />

        {currentTab === "dashboard" && <DashboardWidgets />}

        {/* Global Filters Card */}
        {currentTab === "standard" && (
          <Card sx={{ mb: 2 }}>
            <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                <Typography
                  variant="h6"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FilterListIcon />
                  Report Filters
                </Typography>
                <Button
                  variant="text"
                  onClick={() => setShowFilters(!showFilters)}
                  endIcon={showFilters ? <RemoveIcon /> : <AddIcon />}>
                  {showFilters ? "Hide" : "Show"} Filters
                </Button>
              </Box>
            </Box>

            <Collapse in={showFilters}>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="From Date"
                      type="date"
                      value={reportFilters.dateFrom}
                      onChange={(e) =>
                        setReportFilters({
                          ...reportFilters,
                          dateFrom: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      label="To Date"
                      type="date"
                      value={reportFilters.dateTo}
                      onChange={(e) =>
                        setReportFilters({
                          ...reportFilters,
                          dateTo: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={reportFilters.gender}
                        label="Gender"
                        onChange={(e) =>
                          setReportFilters({
                            ...reportFilters,
                            gender: e.target.value,
                          })
                        }>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Service Type</InputLabel>
                      <Select
                        value={reportFilters.serviceType}
                        label="Service Type"
                        onChange={(e) =>
                          setReportFilters({
                            ...reportFilters,
                            serviceType: e.target.value as string,
                          })
                        }>
                        <MenuItem value="">All</MenuItem>
                        {(appointmentTypes ?? []).map((type) => (
                          <MenuItem key={type.id} value={type.name}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Practitioner</InputLabel>
                      <Select
                        value={reportFilters.practitionerId || ""}
                        label="Practitioner"
                        onChange={(e) =>
                          setReportFilters({
                            ...reportFilters,
                            practitionerId: Number(e.target.value),
                          })
                        }>
                        <MenuItem value="">All</MenuItem>
                        {(practitioners ?? []).map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Location</InputLabel>
                      <Select
                        value={reportFilters.locationId || ""}
                        label="Location"
                        onChange={(e) =>
                          setReportFilters({
                            ...reportFilters,
                            locationId: Number(e.target.value),
                          })
                        }>
                        <MenuItem value="">All</MenuItem>
                        {(clinics ?? []).map((clinic) => (
                          <MenuItem key={clinic.id} value={clinic.id}>
                            {clinic.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Brand</InputLabel>
                      <Select
                        value={reportFilters.brand}
                        label="Brand"
                        onChange={(e) =>
                          setReportFilters({
                            ...reportFilters,
                            brand: e.target.value as string,
                          })
                        }>
                        <MenuItem value="">All</MenuItem>
                        {brands.map((b) => (
                          <MenuItem key={b.id} value={b.name}>
                            {b.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        startIcon={<ClearIcon />}
                        onClick={clearReportFilters}
                        size="small">
                        Clear Filters
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Card>
        )}

        {/* Main Content */}
        {currentTab === "standard" && (
          <Box>
            <Stack spacing={2}>
              {REPORT_CATEGORIES.map((category) => (
                <Accordion
                  key={category.title}
                  expanded={!!expandedAccordions[category.title]}
                  onChange={() =>
                    setExpandedAccordions({
                      ...expandedAccordions,
                      [category.title]: !expandedAccordions[category.title],
                    })
                  }>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography
                      variant="subtitle1"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span style={{ fontSize: "1.2rem" }}>
                        {category.icon}
                      </span>
                      {category.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={3}>
                      {category.sections.map((section) => (
                        <Grid item xs={12} md={6} key={section.title}>
                          <Card
                            variant="outlined"
                            sx={{ p: 2, height: "100%" }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {section.title}
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Stack spacing={1}>
                              {section.items.map((item) => (
                                <Box
                                  key={item}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    p: 1,
                                    borderRadius: 1,
                                    "&:hover": {
                                      bgcolor: "action.hover",
                                    },
                                  }}>
                                  <Typography variant="body2">
                                    {item}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5}>
                                    <Tooltip title="Preview">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handlePreviewReport(
                                            section.title,
                                            category.title,
                                            item,
                                          )
                                        }
                                        disabled={
                                          previewingReport ===
                                          `${category.title}-${section.title}-${item}`
                                        }>
                                        {previewingReport ===
                                        `${category.title}-${section.title}-${item}` ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <PreviewIcon fontSize="inherit" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download PDF">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleStandardReportDownload(
                                            section.title,
                                            category.title,
                                            item,
                                            "pdf",
                                          )
                                        }
                                        disabled={
                                          downloadingReport ===
                                          `${category.title}-${section.title}-${item}`
                                        }>
                                        {downloadingReport ===
                                        `${category.title}-${section.title}-${item}` ? (
                                          <CircularProgress size={16} />
                                        ) : (
                                          <PictureAsPdfIcon fontSize="inherit" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download CSV">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          handleStandardReportDownload(
                                            section.title,
                                            category.title,
                                            item,
                                            "csv",
                                          )
                                        }
                                        disabled={
                                          downloadingReport ===
                                          `${category.title}-${section.title}-${item}`
                                        }>
                                        <DescriptionIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              ))}
                            </Stack>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </Box>
        )}

        {currentTab === "custom" && <QueryBuilder />}

        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="lg"
          fullWidth
          scroll="paper">
          <DialogTitle>
            Report Preview - {previewData?.metadata.reportInfo.reportName}
          </DialogTitle>
          <DialogContent>
            {previewData ? (
              <Box sx={{ pt: 1 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom>
                    Total Records: {previewData.metadata.totalRecords} |
                    Generated:{" "}
                    {new Date(
                      previewData.metadata.generatedAt,
                    ).toLocaleString()}
                  </Typography>
                  <Chip
                    label={`Section: ${previewData.metadata.reportInfo.reportSection}`}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={`Category: ${previewData.metadata.reportInfo.reportCategory}`}
                    size="small"
                  />
                </Box>

                <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {previewData.headers.map((header, index) => (
                          <TableCell key={index}>{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.data.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {previewData.headers.map((header, cellIndex) => {
                            // Find the corresponding value for this header
                            const value = Object.values(row)[cellIndex];
                            return (
                              <TableCell key={cellIndex}>
                                {value !== null && value !== undefined
                                  ? value.toString()
                                  : ""}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {previewData.metadata.isPreview && (
                  <Box sx={{ mt: 2, textAlign: "center" }}>
                    <Chip
                      icon={<PreviewIcon />}
                      label={`Preview limited to ${previewData.metadata.previewLimit} records`}
                      color="info"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
            {previewData && (
              <Button
                variant="contained"
                onClick={() => {
                  // Implement download from preview
                  const { reportCategory, reportSection, reportName } =
                    previewData.metadata.reportInfo;
                  handleStandardReportDownload(
                    reportSection,
                    reportCategory,
                    reportName,
                    "pdf",
                  );
                  setPreviewOpen(false);
                }}
                startIcon={<DownloadIcon />}>
                Download Full Report
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={hideSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert
            onClose={hideSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}
